import { isAppleProvider, isTerminalProvider } from "./subscriptionProvider";
import { BLOOMBERG_EMAIL_REGEX } from "../constants/Constants";
import { PROVIDER } from "../constants/v2/Constants";
import { VERMONT_CODE } from "../constants/Regions";

import countries from "../data/countries.json";
import states from "../data/states.json";
import provinces from "../data/provinces.json";

const EMAIL_MAX_LENGTH = 254;
const EMAIL_REGEX = /^([A-Za-z0-9_\-.+])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*[\]{}().\-~`/+;:|<>"'_,?=])/;
const VALID_STRIPE_CHARACTERS_REGEX = /^[a-zA-Z0-9_]*$/;
const MIN_PWD_LENGTH = 6;
const BW_SUBSCRIPTION_ID_VALIDATOR = /^\d{1,10}$/;

export const INVALID_CHARACTERS_REGEX = /[^a-zA-Z0-9-\s.,&!"#$()+/:;=>?@]/;
export const MIN_STRICT_PWD_LENGTH = 8;
export const MAX_STRICT_PWD_LENGTH = 256;

interface BusinessweekLinkSubscription {
    product: string;
    subscriptionId: string;
    provider: PROVIDER;
    source: string;
}

export interface ErrorValue {
    label?: string;
    message: string;
}

export const ERROR_MESSAGES: Record<string, ErrorValue> = {
    EMAIL_ADDRESS_TOO_LONG: {
        label: "invalid_email_address_too_long",
        message: `Email must be shorter than ${ EMAIL_MAX_LENGTH + 1 } characters.`
    },
    INVALID_EMAIL_ADDRESS: {
        label: "invalid_email_address",
        message: "Email format is invalid."
    },
    BLOOMBERG_EMAIL_ADDRESS: {
        label: "bloomberg_email_address",
        message: "BBA clients get free access."
    },
    EMAILS_DO_NOT_MATCH: {
        label: "invalid_confirmation_email.",
        message: "Emails do not match."
    },
    PASSWORD_ENTROPY_LOW: {
        label: "invalid_password_entropy_low",
        message: "Password must contain one of each: uppercase letter, lowercase letter, number and special character."
    },
    INDUSTRY_REQUIRED: {
        label: "industry_required",
        message: "A valid entry for Industry must be selected."
    },
    APP_GO_PIN_REQUIRED: {
        label: "app_go_pin_required",
        message: "APP<GO> PIN is required."
    },
    APP_GO_PIN_INVALID: {
        label: "app_go_pin_invalid",
        message: "APP<GO> PIN is not in the correct format."
    },
    BUSINESSWEEK_ACCOUNT_INVALID: {
        label: "businessweek_id_invalid",
        message: "Account Number is not in the correct format."
    },
    APPLE_ACCOUNT_INVALID: {
        label: "apple_id_invalid",
        message: "Account Number is not in the correct format."
    },
    PASSWORD_TOO_SHORT: {
        label: "invalid_password_too_short",
        message: `Password must be at least ${ MIN_PWD_LENGTH } characters.`
    },
    PASSWORD_STILL_TOO_SHORT: {
        label: "invalid_password_still_too_short",
        message: `Password must be at least ${ MIN_STRICT_PWD_LENGTH } characters.`
    },
    PASSWORD_TOO_LONG: {
        label: "invalid_password_too_long",
        message: `Password must be at most ${ MAX_STRICT_PWD_LENGTH } characters.`
    },
    PASSWORD_TOO_LONG_GENERIC: {
        label: "invalid_password_too_long",
        message: "Unable to log in. Please try again or try resetting your password."
    },
    PASSWORDS_DO_NOT_MATCH: {
        label: "invalid_confirmation_password.",
        message: "Passwords do not match."
    },
    FIELD_CONTAINS_INVALID_CHARACTERS: {
        label: "invalid_characters",
        message: "You may only use valid, english characters for this field"
    },
    FIELD_REQUIRED: {
        label: "required_field",
        message: "This field is required"
    },
    ZIP_CODE_INVALID: {
        label: "invalid_zip_code",
        message: "You must input a valid zip code"
    },
    POSTAL_CODE_INVALID: {
        label: "invalid_postal_code",
        message: "You must input a valid postal code"
    },
    COUNTRY_INVALID: {
        label: "invalid_country",
        message: "You must select a valid country"
    },
    STATE_INVALID: {
        label: "invalid_state",
        message: "You must select a valid state"
    },
    CANCEL_NOTICE_UNCHECKED :{
        label: "cancel_notice_unchecked",
        message: "You must consent to our auto-renewal policies."
    },
    PROVINCE_INVALID: {
        label: "invalid_province",
        message: "You must select a valid province"
    },
    PASSWORD_IS_TOO_COMMON: {
        label: "invalid_password_too_common",
        message: "Some words or phrases used don't match our criteria for a valid password."
    },
    RESET_PASSWORD_KEY_EXPIRED: {
        message: "The reset password link has expired. Please retry."
    },
    RESET_PASSWORD_KEY_INVALID: {
        message: "The reset password link is invalid. Please retry."
    },
    STRIPE_CHARACTERS_INVALID: {
        message: "Stripe token is invalid"
    }
};

export function validateEmail(email: null | string): null | ErrorValue {
    if (!email || !EMAIL_REGEX.test(email)) {
        return ERROR_MESSAGES.INVALID_EMAIL_ADDRESS;
    }

    if (email.length > EMAIL_MAX_LENGTH) {
        return ERROR_MESSAGES.EMAIL_ADDRESS_TOO_LONG;
    }

    return null;
}

export function validateEmailsMatch(confirmationEmail: string, email: string): null | ErrorValue {
    if (confirmationEmail !== email) {
        return ERROR_MESSAGES.EMAILS_DO_NOT_MATCH;
    }

    return null;
}

export function validateBloombergEmail(email: null | string): null | ErrorValue {
    if (email && BLOOMBERG_EMAIL_REGEX.test(email)) {
        return ERROR_MESSAGES.BLOOMBERG_EMAIL_ADDRESS;
    }

    return null;
}

/**
 * This function is used to check existing users logging in.
 */

export function validatePassword(password: string): null | ErrorValue {
    if (!password || password.length < MIN_PWD_LENGTH) {
        return ERROR_MESSAGES.PASSWORD_TOO_SHORT;
    }

    if (password.length > MAX_STRICT_PWD_LENGTH) {
        return ERROR_MESSAGES.PASSWORD_TOO_LONG_GENERIC;
    }

    return null;
}

export function validatePasswordsMatch(confirmationPassword: string, password: string): null | ErrorValue {
    if (confirmationPassword !== password) {
        return ERROR_MESSAGES.PASSWORDS_DO_NOT_MATCH;
    }

    return null;
}

/**
 * This function is used to check new users or users resetting passwords (new flow)
 */
export function validateStricterPasswordLength(password: string): null | ErrorValue {
    if (!password || password.length < MIN_STRICT_PWD_LENGTH) {
        return ERROR_MESSAGES.PASSWORD_STILL_TOO_SHORT;
    }

    if (password.length > MAX_STRICT_PWD_LENGTH) {
        return ERROR_MESSAGES.PASSWORD_TOO_LONG;
    }

    return null;
}

/**
 * This function is used to check new users or users resetting passwords.
 */

export function validateStricterPassword(password: string): null | ErrorValue {
    const lengthError = validateStricterPasswordLength(password);

    if (lengthError) {
        return lengthError;
    }

    if (!PWD_REGEX.test(password)) {
        return ERROR_MESSAGES.PASSWORD_ENTROPY_LOW;
    }

    return null;
}

/**
 * Ensure AppGoPin has been entered
 * (APP<GO> PIN can be either 6 digits or 10 digits)
 * (APP<GO> PIN is 5 digits in staging environment)
 */

export function validateAppGoPin(pin: undefined | number | string): null | ErrorValue {
    if (!pin) {
        return ERROR_MESSAGES.APP_GO_PIN_REQUIRED;
    }

    const pinString = pin.toString();

    if (pinString.length !== 5 && pinString.length !== 6 && pinString.length !== 10) {
        return ERROR_MESSAGES.APP_GO_PIN_INVALID;
    }

    return null;
}

/**
 * Businessweek account numbers can have the following formats:
 * CDS: 10 digits
 *
 * Some test account numbers do NOT follow the above format.
 */
export function validateBusinessweekId(subscriptionId: undefined | number | string): null | ErrorValue {
    if (!subscriptionId || !BW_SUBSCRIPTION_ID_VALIDATOR.test(subscriptionId.toString())) {
        return ERROR_MESSAGES.BUSINESSWEEK_ACCOUNT_INVALID;
    }

    return null;
}

export function validateSubscriptionInfo(subscription: BusinessweekLinkSubscription): null | ErrorValue {
    const {
        provider,
        subscriptionId
    } = subscription;

    if (isAppleProvider(provider)) {
        return null;
    }

    if (isTerminalProvider(provider)) {
        return validateAppGoPin(subscriptionId);
    }

    return validateBusinessweekId(subscriptionId);
}

export function validatePostalCode(country: string, postalCode: string): null | ErrorValue {
    if (country === "CA" && !postalCode) {
        return ERROR_MESSAGES.POSTAL_CODE_INVALID;
    }

    if (country === "US" && !postalCode) {
        return ERROR_MESSAGES.ZIP_CODE_INVALID;
    }

    return validateValidCharacters(postalCode);
}

export function validateCancelNoticeChecked(billingAddressState: string, shippingAddressState: string,
    cancelNoticeChecked: boolean): null | ErrorValue {
    if ((billingAddressState === VERMONT_CODE || shippingAddressState === VERMONT_CODE)
        && !cancelNoticeChecked) {
        return ERROR_MESSAGES.CANCEL_NOTICE_UNCHECKED;
    }

    return null;
}

export function countryRequiresPostalCodeAndState(country: string): boolean {
    return country === "US" || country === "CA";
}

export function validateCountry(country: string): null | ErrorValue {
    if (!findCodeInArray(countries, country)) {
        return ERROR_MESSAGES.COUNTRY_INVALID;
    }

    return null;
}

export function validateStateOrProvince(country: string, stateOrProvince: string): null | ErrorValue {
    if (country === "CA" && !findCodeInArray(provinces, stateOrProvince)) {
        return ERROR_MESSAGES.PROVINCE_INVALID;
    }

    if (country === "US" && !findCodeInArray(states, stateOrProvince)) {
        return ERROR_MESSAGES.STATE_INVALID;
    }

    return null;
}

function findCodeInArray(array: {key: string; value: string }[], valueToFind: string): boolean {
    for (const item of array) {
        if (item.value === valueToFind) {
            return true;
        }
    }

    return false;
}

export function validateStripeCharacters(stripeId: string): null | ErrorValue {
    if (!VALID_STRIPE_CHARACTERS_REGEX.test(stripeId)) {
        return ERROR_MESSAGES.STRIPE_CHARACTERS_INVALID;
    }

    return null;
}

export function validateValidCharacters(fieldValue?: string): null | ErrorValue {
    if (INVALID_CHARACTERS_REGEX.test(fieldValue || "")) {
        return ERROR_MESSAGES.FIELD_CONTAINS_INVALID_CHARACTERS;
    }

    return null;
}

export function validateFieldIsRequired(fieldValue: string): null | ErrorValue {
    if (!fieldValue) {
        return ERROR_MESSAGES.FIELD_REQUIRED;
    }

    return null;
}

export default {
    validateEmail,
    validateEmailsMatch,
    validateBloombergEmail,
    validatePassword,
    validatePasswordsMatch,
    validateStricterPassword,
    validateStricterPasswordLength,
    validateSubscriptionInfo,
    validateAppGoPin,
    validateBusinessweekId,
    validateCountry,
    validateStateOrProvince,
    validateCancelNoticeChecked,
    validatePostalCode,
    validateStripeCharacters,
    validateValidCharacters,
    validateFieldIsRequired
};
